// ─── TermsOfService.jsx ───────────────────────────────────────────────────────
// Route: /terms
// Add to App.jsx:
//   import TermsOfService from './components/legal/TermsOfService';
//   <Route path="/terms" element={<TermsOfService />} />
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "../Logo";

const LAST_UPDATED = "June 2025";

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 36 }}>
    <h2 style={{
      fontSize: 16, fontWeight: 800, color: "#f8fafc",
      margin: "0 0 12px", letterSpacing: "-0.01em",
      paddingBottom: 10, borderBottom: "1px solid #1e1e1e",
    }}>{title}</h2>
    {children}
  </div>
);

const P = ({ children }) => (
  <p style={{ fontSize: 13, color: "#64748b", lineHeight: 1.85, marginBottom: 12 }}>{children}</p>
);

const UL = ({ items }) => (
  <ul style={{ paddingLeft: 18, marginBottom: 12 }}>
    {items.map((item, i) => (
      <li key={i} style={{ fontSize: 13, color: "#64748b", lineHeight: 1.85, marginBottom: 4 }}>{item}</li>
    ))}
  </ul>
);

const Highlight = ({ children }) => (
  <div style={{
    background: "#111", border: "1px solid #1e1e1e",
    borderRadius: 10, padding: "14px 18px", marginBottom: 16,
    borderLeft: "3px solid #a78bfa",
  }}>
    {children}
  </div>
);

const WarningBox = ({ children }) => (
  <div style={{
    background: "#1a0800", border: "1px solid #f9731633",
    borderRadius: 10, padding: "14px 18px", marginBottom: 16,
  }}>
    {children}
  </div>
);

export default function TermsOfService() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Terms of Service — 100ViewsOnly";
  }, []);

  return (
    <div style={{
      background: "#0a0a0a", minHeight: "100vh",
      fontFamily: "'Inter', system-ui, sans-serif", color: "#e2e8f0",
    }}>
      {/* Nav */}
      <div style={{
        background: "#0a0a0a", borderBottom: "1px solid #1e1e1e",
        padding: "14px 24px", display: "flex", alignItems: "center",
        justifyContent: "space-between", position: "sticky", top: 0, zIndex: 10,
      }}>
        <span onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
          <Logo dark />
        </span>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: "none", border: "1px solid #1e1e1e",
            borderRadius: 20, padding: "5px 14px",
            color: "#475569", fontSize: 11, fontWeight: 600,
            cursor: "pointer", fontFamily: "inherit",
          }}
        >
          ← Back
        </button>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "48px 24px 80px" }}>

        {/* Header */}
        <div style={{ marginBottom: 40 }}>
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: "0.18em",
            color: "#a78bfa", textTransform: "uppercase", display: "block", marginBottom: 10,
          }}>Legal</span>
          <h1 style={{
            fontSize: 32, fontWeight: 900, color: "#fff",
            letterSpacing: "-0.02em", margin: "0 0 8px", lineHeight: 1.1,
          }}>Terms of Service</h1>
          <p style={{ fontSize: 12, color: "#334155", margin: 0 }}>
            Last updated: {LAST_UPDATED} · Governed by the laws of Ontario, Canada
          </p>
        </div>

        <Highlight>
          <p style={{ fontSize: 13, color: "#94a3b8", margin: 0, lineHeight: 1.7 }}>
            <strong style={{ color: "#fff" }}>Plain English summary:</strong> Use the platform fairly. Creators get paid for their content. Fans pay to access exclusive content. We take a platform fee. Don't post illegal content. Canadian creators only. These terms are governed by Ontario law.
          </p>
        </Highlight>

        <Section title="1. Acceptance of Terms">
          <P>By creating an account or joining the waitlist on 100ViewsOnly you agree to these Terms of Service. If you do not agree, do not use the platform. These terms form a binding legal agreement between you and 100ViewsOnly (operated by Viktor, Parkhill, Ontario, Canada).</P>
        </Section>

        <Section title="2. Eligibility">
          <UL items={[
            "You must be at least 18 years of age to use 100ViewsOnly",
            "Creators must be Canadian residents with a Canadian bank account for payouts",
            "Fans and subscribers may be from any country",
            "You must provide accurate information when creating your account",
            "One account per person — duplicate accounts may be removed",
          ]} />
        </Section>

        <Section title="3. Platform Fees">
          <p style={{ fontSize: 12, fontWeight: 700, color: "#94a3b8", margin: "0 0 8px", textTransform: "uppercase", letterSpacing: "0.08em" }}>Standard fee</p>
          <P>100ViewsOnly charges a platform fee of <strong style={{ color: "#fff" }}>10% of all creator earnings</strong> processed through the platform. This fee covers payment processing, platform infrastructure, and operations.</P>
          <p style={{ fontSize: 12, fontWeight: 700, color: "#94a3b8", margin: "0 0 8px", textTransform: "uppercase", letterSpacing: "0.08em" }}>Founding creator rate</p>
          <Highlight>
            <p style={{ fontSize: 13, color: "#94a3b8", margin: 0, lineHeight: 1.7 }}>
              <strong style={{ color: "#fff" }}>Permanent guarantee:</strong> Creators who join during the founding period are permanently locked into a reduced fee rate as communicated in their approval email. This rate is guaranteed for the lifetime of their account and will not increase regardless of future platform pricing changes.
            </p>
          </Highlight>
          <p style={{ fontSize: 12, fontWeight: 700, color: "#94a3b8", margin: "0 0 8px", textTransform: "uppercase", letterSpacing: "0.08em" }}>Payment processing</p>
          <P>All payments are processed by Stripe. Standard Stripe processing fees apply and are separate from the platform fee. Payouts are made in Canadian dollars (CAD) to Canadian bank accounts via Stripe Connect.</P>
        </Section>

        <Section title="4. Creator Responsibilities">
          <UL items={[
            "You own all content you post and are solely responsible for it",
            "You grant 100ViewsOnly a limited licence to display your content to subscribers on the platform",
            "You must not post content that is illegal under Canadian law",
            "You must not post content that infringes third-party intellectual property rights",
            "You must accurately represent your content and not mislead subscribers",
            "You are responsible for reporting your earnings to the CRA for tax purposes",
            "You must maintain a valid Stripe Connect account to receive payouts",
          ]} />
        </Section>

        <Section title="5. Fan and Subscriber Responsibilities">
          <UL items={[
            "Subscriptions are personal and non-transferable",
            "You may not redistribute, repost, or share exclusive content without creator permission",
            "You may not record, screenshot, or reproduce exclusive content for distribution",
            "You must not harass, threaten, or abuse creators or other users",
          ]} />
        </Section>

        <Section title="6. Prohibited Content">
          <WarningBox>
            <p style={{ fontSize: 12, fontWeight: 700, color: "#f97316", margin: "0 0 8px" }}>The following content is strictly prohibited:</p>
            <UL items={[
              "Any content involving minors in a sexual or exploitative context",
              "Non-consensual intimate imagery",
              "Content that promotes violence, hatred, or discrimination",
              "Spam, scams, or misleading commercial content",
              "Content that violates any applicable Canadian law",
              "Content that infringes the intellectual property rights of others",
            ]} />
          </WarningBox>
          <P>Violation of these rules will result in immediate account termination and may be reported to relevant authorities.</P>
        </Section>

        <Section title="7. Refund Policy">
          <P>Subscription payments are generally non-refundable once a subscriber has accessed exclusive content. Refunds may be issued at our discretion in cases of billing errors or platform failures. Fans may cancel subscriptions at any time — access continues until the end of the current billing period.</P>
        </Section>

        <Section title="8. Account Termination">
          <P>We reserve the right to suspend or terminate any account that violates these terms, at our sole discretion, with or without notice. Creators whose accounts are terminated will receive any outstanding earned balance minus applicable fees, provided the termination was not due to fraud or policy violations.</P>
        </Section>

        <Section title="9. Limitation of Liability">
          <P>100ViewsOnly is provided "as is." We are not liable for any indirect, incidental, or consequential damages arising from your use of the platform. Our total liability to you for any claim shall not exceed the total fees you paid to us in the 3 months prior to the claim.</P>
        </Section>

        <Section title="10. Governing Law">
          <P>These Terms are governed by the laws of the Province of Ontario and the federal laws of Canada applicable therein. Any disputes shall be resolved in the courts of Ontario. If you are located outside Canada, you agree to submit to the jurisdiction of Ontario courts.</P>
        </Section>

        <Section title="11. Changes to Terms">
          <P>We may update these terms as the platform evolves. We will notify you of material changes by email at least 30 days before they take effect. Continued use of the platform after that date constitutes acceptance.</P>
        </Section>

        <Section title="12. Contact">
          <P>
            Legal questions: <a href="mailto:support@100viewsonly.ca" style={{ color: "#a78bfa" }}>support@100viewsonly.ca</a><br />
            100ViewsOnly · Parkhill, Ontario, Canada
          </P>
        </Section>

      </div>
    </div>
  );
}
